import React, { Suspense, useCallback } from 'react';
import type { AttackPathsData } from './markdown/AttackPathGraph';
import { useChatStore } from '../../store/chatStore';
import { SearchAttackPathNodeTooltip } from './AttackPathNodeMeta/SearchAttackPathNodeTooltip';
import { ErrorBoundary } from './markdown/ErrorBoundary';

import { useTranslation } from 'react-i18next';

const AttackPathLazy = React.lazy(
  () =>
    import('../../../../../../findings/src/components/FindingsTable/ContextualView/AttackPathComponent'),
);

interface AttackPathViewProps {
  attackPaths: AttackPathsData;
  /** Override node click. When set, replaces default findings slide-over (e.g. send message to Pleri in search). */
  onNodeClick?: (node: {
    id?: string;
    label?: string;
    [key: string]: unknown;
  }) => void;
  /** Override node tooltip. When set, replaces default findings tooltip (e.g. full meta + "Click to ask Pleri" in search). */
  nodeTooltipComponent?: React.ComponentType<{
    item: { id?: string; label?: string; [key: string]: unknown };
    closeTooltip: () => void;
  }>;
}

/** Fixed height for the attack path graph (matches findings MFE inner container). Export for loading skeleton. */
export const ATTACK_PATH_CONTAINER_HEIGHT = 420;

/** Structured prompt sent to Pleri when user clicks an attack path node (same pattern as PleriActionCell / Mermaid). */
function buildExplainAssetMessage(prn: string): string {
  return `<usermessage>Explain this asset in the context of the attack path please.</usermessage><context>${prn}</context>`;
}

/** Renders attack path graph (findings MFE). Override: send message to Pleri on click; custom tooltip in search. */
export const AttackPathView: React.FC<AttackPathViewProps> = ({
  attackPaths,
  onNodeClick,
  nodeTooltipComponent,
}) => {
  const { t } = useTranslation('pleri/chat');
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const addMessage = useChatStore((state) => state.addMessage);
  const createConversation = useChatStore((state) => state.createConversation);

  const handleNodeClick = useCallback(
    (node: {
      id?: string;
      label?: string;
      prn?: string;
      [key: string]: unknown;
    }) => {
      const prn = typeof node.prn === 'string' ? node.prn : undefined;
      if (!prn || !prn.startsWith('prn:assets')) {
        return;
      }

      const messageText = buildExplainAssetMessage(prn);
      if (!activeConversationId) {
        const newId = createConversation();
        addMessage(newId, {
          content: [{ type: 'text', text: messageText }],
          role: 'user',
        });
      } else {
        addMessage(activeConversationId, {
          content: [{ type: 'text', text: messageText }],
          role: 'user',
        });
      }
    },
    [activeConversationId, addMessage, createConversation],
  );

  const fallback = (
    <div
      className="flex h-full min-h-full items-center justify-center rounded-xl bg-red-50 p-4 text-center text-sm text-red-700 dark:bg-red-950"
      style={{ height: ATTACK_PATH_CONTAINER_HEIGHT }}
    >
      {t('attackPathFailedToLoadTheFindings')}
    </div>
  );

  return (
    <div
      className="my-4 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      style={{
        height: ATTACK_PATH_CONTAINER_HEIGHT,
        minHeight: ATTACK_PATH_CONTAINER_HEIGHT,
      }}
    >
      <ErrorBoundary fallback={fallback}>
        <Suspense
          fallback={
            <div
              className="h-full w-full rounded-xl bg-gray-50 dark:bg-gray-800"
              style={{ height: ATTACK_PATH_CONTAINER_HEIGHT }}
            />
          }
        >
          <AttackPathLazy
            attackPaths={attackPaths as any}
            onNodeClick={(onNodeClick ?? handleNodeClick) as any}
            nodeTooltipComponent={
              (nodeTooltipComponent ?? SearchAttackPathNodeTooltip) as any
            }
          />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};
